import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { RemoveLocalStorage } from '@/shared/utils/LocalStorage.utils';
import { Storage } from '@/shared/types/enums/Storage.enum';
import { Routes } from '@/shared/types/enums/Routes.enum';

/**
 * Hook que limpia el localStorage cuando el usuario sale del flujo de registro
 * Detecta si la ruta actual NO pertenece a ningún flujo de registro activo
 * 
 * @param clearFilesCallback - Función opcional para limpiar archivos del context (usada solo en rutas de entidades)
 */
export const useRegistrationFlowCleanup = (clearFilesCallback?: () => void) => {
    const location = useLocation();

    useEffect(() => {
        // Normalizar ruta actual (minusculas y sin slash final)
        const normalizePath = (path: string) => path.toLowerCase().replace(/\/$/, '');
        const currentPath = normalizePath(location.pathname);

        // Rutas que pertenecen a los flujos de registro
        const registrationFlowPaths = [
            // Persona Física - Ciudadano
            Routes.REGISTRO_PERSONA_FISICA_CIUDADANO_IDENTIFICACION,
            Routes.REGISTRO_PERSONA_FISICA_CIUDADANO_CREDENCIALES,
            Routes.REGISTRO_PERSONA_FISICA_CIUDADANO_OTP,
            Routes.REGISTRO_PERSONA_FISICA_CIUDADANO_TERMINOS,
            
            // Persona Física - Abogado
            Routes.REGISTRO_PERSONA_FISICA_ABOGADO_IDENTIFICACION,
            Routes.REGISTRO_PERSONA_FISICA_ABOGADO_DATOS_PROFESIONALES,
            Routes.REGISTRO_PERSONA_FISICA_ABOGADO_CREDENCIALES,
            Routes.REGISTRO_PERSONA_FISICA_ABOGADO_OTP,
            Routes.REGISTRO_PERSONA_FISICA_ABOGADO_TERMINOS,
            
            // Persona Jurídica - Jurídica
            Routes.REGISTRO_PERSONA_JURIDICA_IDENTIFICACION,
            Routes.REGISTRO_PERSONA_JURIDICA_DATOS,
            Routes.REGISTRO_PERSONA_JURIDICA_DOCUMENTOS,
            Routes.REGISTRO_PERSONA_JURIDICA_CONTACTOS,
            Routes.REGISTRO_PERSONA_JURIDICA_TERMINOS,
            
            // Persona Jurídica - Gubernamental
            Routes.REGISTRO_PERSONA_JURIDICA_GUBERNAMENTAL_IDENTIFICACION,
            Routes.REGISTRO_PERSONA_JURIDICA_GUBERNAMENTAL_DATOS,
            Routes.REGISTRO_PERSONA_JURIDICA_GUBERNAMENTAL_DOCUMENTOS,
            Routes.REGISTRO_PERSONA_JURIDICA_GUBERNAMENTAL_CONTACTOS,
            Routes.REGISTRO_PERSONA_JURIDICA_GUBERNAMENTAL_TERMINOS,
        ].map(normalizePath);

        // Rutas de selección de tipo (mantienen el localStorage para permitir volver atrás)
        // Se deja vacío o solo con rutas muy específicas si se desea persistencia fuera del flujo.
        // Según requerimiento de seguridad, al volver a /registro/pj se debe limpiar.
        const selectionPaths: string[] = [
            // Routes.REGISTRO,
            // Routes.REGISTRO_PERSONA_FISICA,
            // Routes.REGISTRO_PERSONA_JURIDICA,
        ].map(normalizePath);

        // Verificar si la ruta actual está en algún flujo de registro o selección
        const isInRegistrationFlow = registrationFlowPaths.some(path => currentPath === path);
        const isInSelection = selectionPaths.some(path => currentPath === path);

        // Si NO está en ningún flujo de registro ni en selección, limpiar localStorage
        if (!isInRegistrationFlow && !isInSelection) {

            RemoveLocalStorage(Storage.FormRegistro);
            
            // Si se proporcionó callback para limpiar archivos (context de entidades), ejecutarlo
            if (clearFilesCallback) {
                clearFilesCallback();
            }
        }

    }, [location.pathname, clearFilesCallback]);
};
